import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Trash2, Play, HardDrive, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { offlineStorage, getAllDownloads, deleteDownload } from '../utils/offlineStorage';

const DownloadedContent = () => {
  const [downloads, setDownloads] = useState([]);
  const [storageInfo, setStorageInfo] = useState({ totalSize: 0, totalFiles: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadDownloads();
  }, []);

  const loadDownloads = async () => {
    try {
      const allDownloads = await getAllDownloads();
      setDownloads(allDownloads);

      const usage = await offlineStorage.getStorageUsage();
      setStorageInfo(usage);
    } catch (error) {
      console.error('Error loading downloads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this downloaded content?')) return;

    try {
      await deleteDownload(id);
      await loadDownloads();
    } catch (error) {
      console.error('Error deleting download:', error);
      alert('Failed to delete download');
    }
  };

  const handlePlay = (download) => {
    // Navigate to player with offline content
    if (download.type === 'episode') {
      navigate(`/player/offline/series/${download.seriesId}?episode=${download.episodeId}`);
    } else if (download.type === 'chapter') {
      navigate(`/player/offline/audiobook/${download.seriesId}?chapter=${download.chapterId}`);
    } else {
      navigate(`/player/offline/audio/${download.contentId}`);
    }
  };

  const groupedDownloads = downloads.reduce((acc, download) => {
    const key = download.seriesId || download.contentId;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(download);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-purple-900 p-6 flex items-center justify-center">
        <div className="text-white text-xl">Loading downloads...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-purple-900 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Downloaded Content</h1>
            <p className="text-gray-400">Access your content offline</p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="text-white border-slate-600"
          >
            ← Back
          </Button>
        </div>

        {/* Storage Info */}
        <Card className="bg-slate-800/50 border-slate-700 mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <HardDrive className="w-8 h-8 text-indigo-400" />
              <div className="flex-1">
                <p className="text-white font-semibold">Storage Used</p>
                <p className="text-gray-400 text-sm">
                  {offlineStorage.formatBytes(storageInfo.totalSize)} • {storageInfo.totalFiles} files
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {downloads.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-12 text-center">
              <AlertCircle className="w-16 h-16 mx-auto mb-4 text-gray-500" />
              <h3 className="text-xl font-semibold text-white mb-2">No Downloaded Content</h3>
              <p className="text-gray-400 mb-6">
                Download series, audiobooks, or single files to access them offline
              </p>
              <Button onClick={() => navigate('/dashboard')} className="bg-indigo-600 hover:bg-indigo-700">
                Browse Library
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedDownloads).map(([key, items]) => {
              const firstItem = items[0];
              const isSeries = firstItem.type === 'episode' || firstItem.type === 'chapter';

              return (
                <Card key={key} className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center justify-between">
                      <span>
                        {isSeries ? 
                          `${firstItem.type === 'episode' ? 'Cinematic' : 'Audiobook'} Series (${items.length} ${firstItem.type}s)` :
                          firstItem.title
                        }
                      </span>
                      {isSeries && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            if (confirm(`Delete all ${items.length} ${firstItem.type}s?`)) {
                              items.forEach(item => handleDelete(item.id));
                            }
                          }}
                          className="text-red-400 border-red-400 hover:bg-red-900/20"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete All
                        </Button>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {items.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700"
                        >
                          <div className="flex-1">
                            <p className="text-white font-medium">{item.title}</p>
                            <p className="text-sm text-gray-400">
                              {item.filename} • Downloaded {new Date(item.downloadedAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handlePlay(item)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Play className="w-4 h-4 mr-2" />
                              Play
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(item.id)}
                              className="text-red-400 border-red-400 hover:bg-red-900/20"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DownloadedContent;
